import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getTrends } from '../../api/client';
import {
  ComposedChart, LineChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';

const fmt  = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtK = (n) => Math.abs(n) >= 1000 ? `${n < 0 ? '-' : ''}$${(Math.abs(n) / 1000).toFixed(1)}k` : fmt(n);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthKeyLabel = (key) => { const [y, m] = String(key).split('-'); return `${MONTHS[+m - 1]} '${String(y).slice(2)}`; };
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// This is what actually catches price creep and spending spikes as a visual over time —
// tonight's Eating Out spike or a subscription's slow climb only show up here, not in a
// single period's Deep Dive numbers alone.
const Trends = () => {
  const { COLORS } = useTheme();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(''); // 'expense:<id>' or 'income:<id>'

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getTrends({ periods: 12, today: localToday() });
        setPeriods(res.data.periods || []);
      } catch (e) { setLoadError(true); }
      setLoading(false);
    })();
  }, []);

  // Same category set every period (retrospective always walks the household's CURRENT
  // categories/sources) — so any period's list is representative; use the most recent.
  const categoryOptions = useMemo(() => {
    const last = periods[periods.length - 1];
    if (!last) return { expense: [], income: [] };
    return { expense: last.expenseCategories, income: last.incomeSources };
  }, [periods]);

  const overviewData = useMemo(() => periods.map((p) => ({
    label: monthKeyLabel(p.key),
    Income: p.actualIncome,
    Expenses: p.actualExpense,
    Net: p.actualNet,
  })), [periods]);

  const [selType, selId] = selected ? selected.split(':') : [null, null];
  const categoryData = useMemo(() => {
    if (!selType) return [];
    return periods.map((p) => {
      const list = selType === 'expense' ? p.expenseCategories : p.incomeSources;
      const line = list.find((l) => String(l.id) === selId);
      return { label: monthKeyLabel(p.key), Planned: line?.planned || 0, Actual: line?.actual || 0 };
    });
  }, [periods, selType, selId]);

  const selectedName = useMemo(() => {
    const list = selType === 'expense' ? categoryOptions.expense : categoryOptions.income;
    return list.find((l) => String(l.id) === selId)?.name || '';
  }, [categoryOptions, selType, selId]);

  const inp = { padding: '10px 12px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
        <div style={{ color: COLORS.textMuted, marginBottom: 4, fontWeight: 700 }}>{label}</div>
        {payload.map((p) => (
          <div key={p.dataKey} style={{ color: p.color, fontFamily: 'monospace' }}>{p.name}: {fmt(p.value)}</div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <h2 style={{ color: COLORS.text, fontSize: 24, fontWeight: 800, margin: '0 0 4px', letterSpacing: -0.5 }}>Trends</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 14, margin: '0 0 16px' }}>
        Income, expenses, and net across recent periods — same real numbers as the Monthly Deep Dive, just over time.
      </p>

      {loading ? (
        <p style={{ color: COLORS.textMuted }}>Loading…</p>
      ) : loadError ? (
        <p style={{ color: COLORS.red }}>Couldn't load trends. Try again in a moment.</p>
      ) : periods.length === 0 ? (
        <p style={{ color: COLORS.textMuted }}>Not enough history yet.</p>
      ) : (
        <>
          {/* Overview: Income vs Expenses (bars) + Net (line) across periods */}
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '16px 12px 8px', marginBottom: 16 }}>
            <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, paddingLeft: 6 }}>
              Income vs. Expenses
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={overviewData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={COLORS.border} />
                <XAxis dataKey="label" tick={{ fill: COLORS.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fmtK} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Income" fill={COLORS.green} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill={COLORS.red} radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="Net" stroke={COLORS.accent} strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Per-category picker: this is what actually surfaces a slow price creep or a
              spike as a visual, instead of only being spottable by comparing two periods'
              numbers by hand in the Deep Dive text. */}
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '16px 12px 8px' }}>
            <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, paddingLeft: 6 }}>
              Category Trend
            </div>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} style={{ ...inp, width: '100%', marginBottom: 12 }}>
              <option value="">Pick a category or income source…</option>
              {categoryOptions.expense.length > 0 && (
                <optgroup label="Expenses">
                  {categoryOptions.expense.map((c) => <option key={`expense:${c.id}`} value={`expense:${c.id}`}>{c.name}</option>)}
                </optgroup>
              )}
              {categoryOptions.income.length > 0 && (
                <optgroup label="Income">
                  {categoryOptions.income.map((c) => <option key={`income:${c.id}`} value={`income:${c.id}`}>{c.name}</option>)}
                </optgroup>
              )}
            </select>

            {selected ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={categoryData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={COLORS.border} />
                  <XAxis dataKey="label" tick={{ fill: COLORS.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fmtK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Planned" stroke={COLORS.textMuted} strokeWidth={1.5} strokeDasharray="5 3" dot={false} name={`${selectedName} (planned)`} />
                  <Line type="monotone" dataKey="Actual" stroke={COLORS.accent} strokeWidth={2.5} dot={{ r: 3 }} name={`${selectedName} (actual)`} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: COLORS.textMuted, fontSize: 13, padding: '4px 6px 14px' }}>Pick a category above to see how it's moved period to period.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Trends;
